<template>
    <div>
        <consultation-card v-for="(consultation, i) in consultations"
                           @click="showEncounter(consultation.id)"
                           :class="i === 0 ? 'mx-3' : 'marginer'"
                           :has-children="false"
                           :name="consultation.practitioner.first_name + ' ' + consultation.practitioner.last_name"
                           :summary="consultation.encounter_diagnose ? (consultation.encounter_diagnose.condition_id ? String(consultation.encounter_diagnose.condition[nameKey]) : consultation.other_condition) : '-'"
                           :date="consultation.created_at" :key="consultation.id"></consultation-card>

        <b-modal id="details" :title="t('More information')" scrollable centered hide-footer header-class="pl-4"
                 @hidden="consultation = null"
                 content-class="border-radius-1">
            <b-overlay :show="show">
                <template v-if="consultation">
                    <div class="d-flex">
                        <div class="mx-2 d-flex align-items-baseline">
                            <i-complaint width="2.5em" height="2.5em"></i-complaint>
                        </div>

                        <div class="d-flex flex-column align-items-start">
                            <small class="d-block font-weight-bolder">{{t('Complaint')}}</small>
                            <small class="d-block">{{consultation.complaint}}</small>
                        </div>
                    </div>

                    <template v-if="consultation.primary_diagnoses">
                        <hr>

                        <div class="d-flex">
                            <div class="mx-2 d-flex align-items-baseline">
                                <i-primary-diagnoses width="2.5em" height="2.5em"></i-primary-diagnoses>
                            </div>

                            <div class="d-flex flex-column align-items-start">
                                <small class="d-block font-weight-bolder">{{t('Primary diagnosis')}}</small>
                                <small class="d-block">{{consultation.primary_diagnoses}}</small>
                            </div>
                        </div>
                    </template>

                    <template v-if="consultation.text_report">
                        <hr>

                        <div class="d-flex">
                            <div class="mx-2 d-flex align-items-baseline">
                                <i-report width="2.5em" height="2.5em"></i-report>
                            </div>

                            <div class="d-flex flex-column align-items-start">
                                <small class="d-block font-weight-bolder">{{t('Report')}}</small>
                                <small class="d-block">{{consultation.text_report}}</small>
                            </div>
                        </div>
                    </template>

                    <template v-if="consultation.medications.length">
                        <hr>

                        <div class="d-flex">
                            <div class="mx-2 d-flex align-items-baseline">
                                <i-medication width="2.5em" height="2.5em"></i-medication>
                            </div>

                            <div class="d-flex flex-column align-items-start">
                                <small class="d-block font-weight-bolder">{{t('Medications')}}</small>
                                <small class="d-block" v-for="encounter_medication in consultation.medications"
                                       :key="encounter_medication.id">{{encounter_medication.medication.name_en}}</small>
                            </div>
                        </div>
                    </template>

                    <template v-if="consultation.labs.length">
                        <hr>

                        <div class="d-flex">
                            <div class="mx-2 d-flex align-items-baseline">
                                <i-test width="2.5em" height="2.5em"></i-test>
                            </div>

                            <div class="d-flex flex-column align-items-start">
                                <small class="d-block font-weight-bolder">{{t('Lab tests')}}</small>
                                <small class="d-block" v-for="lab in consultation.labs"
                                       :key="lab.id">{{lab.name_en}} | {{lab.name_ar}}</small>
                            </div>
                        </div>
                    </template>

                    <template v-if="consultation.radiology.length">
                        <hr>

                        <div class="d-flex">
                            <div class="mx-2 d-flex align-items-baseline">
                                <i-radiology width="2.5em" height="2.5em"></i-radiology>
                            </div>

                            <div class="d-flex flex-column align-items-start">
                                <small class="d-block font-weight-bolder">{{t('Radiology')}}</small>
                                <small class="d-block" v-for="radiology in consultation.radiology"
                                       :key="radiology.id">
                                    {{radiology.name_en}} | {{radiology.name_ar}}</small>
                            </div>
                        </div>
                    </template>

                    <template v-if="consultation.all_media.length">
                        <hr>

                        <small class="d-flex mx-2 mb-2 font-weight-bolder">{{t('Attachments')}}</small>

                        <div class="d-flex mx-2 justify-content-start py-2 overflow-auto">
                            <template v-for="media in consultation.all_media">
                                <component :class="margin" :is="media.mime_type.split('/')[0] + '-thumbnail'"
                                           :src="media.full_url"
                                           @click="showMedia(media)"
                                           :key="media.id"/>
                            </template>
                        </div>
                    </template>
                </template>
                <template v-else>
                    <div style="height: 4rem;"></div>
                </template>
            </b-overlay>
        </b-modal>

        <viewer @close="closeMedia" :media="mediaToShow"/>
    </div>
</template>

<script>
    import ConsultationCard from "../../components/Cards/ConsultationCard";
    import IComplaint from "../../components/Icons/IComplaint";
    import IReport from "../../components/Icons/IReport";
    import IMedication from "../../components/Icons/IMedication";
    import ITest from "../../components/Icons/ITest";
    import IRadiology from "../../components/Icons/IRadiology";
    import IPrimaryDiagnoses from "../../components/Icons/IPrimaryDiagnoses";
    import Viewer from "../../components/Viewer";
    import ImageThumbnail from "../../components/MediaThumbnails/ImageThumbnail";
    import VideoThumbnail from "../../components/MediaThumbnails/VideoThumbnail";
    import ApplicationThumbnail from "../../components/MediaThumbnails/ApplicationThumbnail";

    export default {
        name: "ConsultationWithChildren",
        components: {
            ConsultationCard,
            IComplaint,
            IReport,
            IMedication,
            ITest,
            IRadiology,
            IPrimaryDiagnoses,
            Viewer,
            ImageThumbnail,
            VideoThumbnail,
            ApplicationThumbnail
        },
        inject: ['dataRepo'],
        props: {
            encounter_id: {
                required: true,
                type: Number
            }
        },
        computed: {
            nameKey() {
                return `name_${this.$translate.lang}`;
            },

            margin() {
                return this.$translate.lang === 'en' ? 'mr-3' : 'ml-3'
            },
        },
        methods: {
            showEncounter(id) {
                this.show = true;
                this.$bvModal.show('details')
                this.dataRepo.getEncounterDetails(id).then(({data: consultation}) => {
                    this.consultation = consultation;
                }).catch(err => this.$handleError(err)).finally(() => this.show = false)
            },

            showMedia(media) {
                if (media.mime_type.split('/')[0] === 'application' || media.mime_type.split('/')[1] === 'x-ms-asf') {
                    window.open(media.full_url)
                } else {
                    this.mediaToShow = media
                }
            },

            closeMedia() {
                this.mediaToShow = null
            }
        },
        data: () => ({
            consultations: [],
            consultation: null,
            mediaToShow: null,
            show: false
        }),
        mounted() {
            this.dataRepo.getEncounterChildren(this.encounter_id).then(({data}) => {
                this.consultations = data
            })
        }
    }
</script>

<style scoped>
    .marginer {
        margin-left: 2.2rem !important;
        margin-right: 2.2rem !important;
    }
</style>
